import { useFetch } from '@/composables/useFetch';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { ApprovedDayOffGroup, ApprovedDaysOffByUser, DayOff, DayOffApproval, User } from '@/types/api';
import { differenceInDaysWithExclusions, formatDateForApi, isDayInRange } from '@/utils/date';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface DayOffExclusionOptions {
  excludeWeekends?: boolean;
  excludeDaysOfWeek?: number[];
  excludeDates?: Date[];
}

export interface DayOffGroup {
  id: string;
  daysOff: DayOff[];
  dates: Date[];
  reason: { id: number; name: string } | null;
  info: string | null;
  status: { value: string; label: string; color: string };
  isGroup: boolean;
  startDate: Date;
  endDate: Date;
}

export function useDayOff(currentUser?: Ref<User | null>) {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const { fetchData } = useFetch();
  const envStore = useEnvStore();
  const userStore = useUserStore();

  // Reactive state
  const userDaysOff = ref<DayOff[]>([]);
  const daysOffSummary = ref<{
    allocated_yearly_days: number;
    days_off_taken_count: number;
    remaining_days_off: number;
  } | null>(null);
  const loadingDaysOff = ref(false);
  const pendingApprovals = ref<DayOffApproval[]>([]);
  const approvedDaysOff = ref<ApprovedDaysOffByUser[]>([]);
  const loadingPending = ref(false);
  const loadingApproved = ref(false);

  // Computed properties
  const user = computed(() => currentUser?.value || userStore.user);
  const restaurantId = computed(() => user.value?.active_restaurant?.id || '');

  // Filter out declined days off for UI display
  const visibleDaysOff = computed(() => userDaysOff.value.filter((dayOff) => dayOff.status.value !== 'declined'));

  // Convert DayOff objects to Date objects
  const userDaysOffDates = computed(() => visibleDaysOff.value.map((dayOff) => new Date(dayOff.date)));

  // Group days off by group_id for display
  const groupedDaysOff = computed(() => {
    const groups = new Map<string, DayOff[]>();

    visibleDaysOff.value.forEach((dayOff) => {
      const groupId = dayOff.group_id || `single-${dayOff.id}`;
      if (!groups.has(groupId)) {
        groups.set(groupId, []);
      }
      groups.get(groupId)!.push(dayOff);
    });

    return Array.from(groups.values())
      .map((group) => {
        const sortedGroup = group.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        return {
          id: group[0].group_id || `single-${group[0].id}`,
          daysOff: sortedGroup,
          dates: sortedGroup.map((d) => new Date(d.date)),
          reason: group[0].reason,
          info: group[0].info,
          status: group[0].status,
          isGroup: group.length > 1 && !!group[0].group_id,
          startDate: new Date(sortedGroup[0].date),
          endDate: new Date(sortedGroup[sortedGroup.length - 1].date),
        } as DayOffGroup;
      })
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  });

  const remainingDaysOff = computed(() => {
    return daysOffSummary.value?.remaining_days_off || 0;
  });

  const shouldShowLimitInfo = computed(() => {
    return daysOffSummary.value !== null && daysOffSummary.value !== undefined;
  });

  // Utility functions
  function calculateDaysToTake(selectedRange: { start: Date; end: Date } | null, exclusionOptions: DayOffExclusionOptions = {}): number {
    if (!selectedRange) return 0;

    let diff = differenceInDaysWithExclusions(selectedRange.start, selectedRange.end, exclusionOptions);

    // Subtract already taken days off in the selected range
    for (const dayOff of userDaysOffDates.value) {
      if (isDayInRange(dayOff, selectedRange.start, selectedRange.end)) {
        diff--;
      }
    }

    return Math.max(0, diff);
  }

  function isValidDaysOffRequest(selectedRange: { start: Date; end: Date } | null, exclusionOptions: DayOffExclusionOptions = {}): boolean {
    if (!selectedRange) return false;

    const daysToTake = calculateDaysToTake(selectedRange, exclusionOptions);

    // If limit info is hidden, always allow adding days off
    if (!shouldShowLimitInfo.value) {
      return daysToTake > 0;
    }

    // Check against remaining days
    return remainingDaysOff.value >= daysToTake && daysToTake > 0;
  }

  function generateDisabledDatesForCalendar(exclusionOptions: DayOffExclusionOptions = {}): Date[] {
    const disabled = [...userDaysOffDates.value];

    // Add excluded specific dates
    if (exclusionOptions.excludeDates) {
      disabled.push(...exclusionOptions.excludeDates);
    }

    return disabled.filter((date) => date instanceof Date && !isNaN(date.valueOf()));
  }

  function generateDisabledDaysOfWeek(exclusionOptions: DayOffExclusionOptions = {}): number[] {
    const disabled: number[] = [];

    if (exclusionOptions.excludeWeekends) {
      disabled.push(0, 6); // Sunday and Saturday
    }

    if (exclusionOptions.excludeDaysOfWeek) {
      disabled.push(...exclusionOptions.excludeDaysOfWeek);
    }

    return [...new Set(disabled)]; // Remove duplicates
  }

  // API functions
  async function fetchUserDaysOff(userId?: string): Promise<void> {
    const targetUserId = userId || user.value?.id;
    const targetRestaurantId = restaurantId.value;

    if (!targetUserId || !targetRestaurantId) {
      userDaysOff.value = [];
      daysOffSummary.value = null;
      return;
    }

    loadingDaysOff.value = true;
    try {
      const response = await fetchData(`${envStore.apiUrl}/manager/${targetRestaurantId}/team/${targetUserId}/days-off`);
      if (response.data) {
        userDaysOff.value = response.data.data || [];
        daysOffSummary.value = response.data.summary || null;
      } else {
        userDaysOff.value = [];
        daysOffSummary.value = null;
      }
    } catch (error) {
      console.error("Error loading user's days off:", error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorFetchUserDaysOff'),
        life: 3000,
      });
      userDaysOff.value = [];
      daysOffSummary.value = null;
    } finally {
      loadingDaysOff.value = false;
    }
  }

  async function addDaysOff(dates: Date[], userId?: string): Promise<{ success: boolean; error?: unknown }> {
    const targetUserId = userId || user.value?.id;
    const targetRestaurantId = restaurantId.value;

    if (!targetUserId || !targetRestaurantId || dates.length === 0) {
      return { success: false, error: 'Missing required parameters' };
    }

    const datesPayload = dates.map((date) => formatDateForApi(date));

    try {
      const response = await fetchData(`${envStore.apiUrl}/manager/${targetRestaurantId}/team/${targetUserId}/days-off`, 'POST', {
        dates: datesPayload,
      });

      if (response.error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: (response.error as { message?: string }).message || t('dayOff.errorAdd'),
          life: 3000,
        });
        return { success: false, error: response.error };
      } else {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('dayOff.successAdd'),
          life: 3000,
        });
        // Refresh data
        await fetchUserDaysOff(targetUserId);
        return { success: true };
      }
    } catch (error) {
      console.error('Error adding days off:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorAdd'),
        life: 3000,
      });
      return { success: false, error };
    }
  }

  async function deleteDaysOff(dates: Date[], userId?: string): Promise<{ success: boolean; error?: unknown }> {
    const targetUserId = userId || user.value?.id;
    const targetRestaurantId = restaurantId.value;

    if (!targetUserId || !targetRestaurantId || dates.length === 0) {
      return { success: false, error: 'Missing required parameters' };
    }

    const datesPayload = dates.map((date) => formatDateForApi(date));

    try {
      const response = await fetchData(`${envStore.apiUrl}/manager/${targetRestaurantId}/team/${targetUserId}/days-off`, 'DELETE', {
        dates: datesPayload,
      });

      if (response.error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: (response.error as { message?: string }).message || t('dayOff.errorDelete'),
          life: 3000,
        });
        return { success: false, error: response.error };
      } else {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('dayOff.successDelete'),
          life: 3000,
        });
        // Refresh data
        await fetchUserDaysOff(targetUserId);
        return { success: true };
      }
    } catch (error) {
      console.error('Error deleting days off:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorDelete'),
        life: 3000,
      });
      return { success: false, error };
    }
  }

  // Manager functions
  async function fetchPendingApprovals(): Promise<void> {
    if (!restaurantId.value) return;

    loadingPending.value = true;
    try {
      const response = await fetchData(`${envStore.apiUrl}/manager/${restaurantId.value}/pending-approvals/days-off`);
      pendingApprovals.value = response.data || [];
    } catch (error) {
      console.error('Error loading pending approvals:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorLoadingApprovals'),
        life: 3000,
      });
    } finally {
      loadingPending.value = false;
    }
  }

  async function fetchApprovedDaysOff(): Promise<void> {
    if (!restaurantId.value) return;

    loadingApproved.value = true;
    try {
      const response = await fetchData(`${envStore.apiUrl}/manager/${restaurantId.value}/approved/days-off`);
      approvedDaysOff.value = response.data || [];
    } catch (error) {
      console.error('Error loading approved days off:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorLoadingApproved'),
        life: 3000,
      });
    } finally {
      loadingApproved.value = false;
    }
  }

  async function approveOrDeclineDaysOff(
    group: DayOffApproval,
    action: 'approve' | 'decline',
    comment?: string,
  ): Promise<{ success: boolean; error?: unknown }> {
    if (!restaurantId.value) {
      return { success: false, error: 'Restaurant ID required' };
    }

    const actionEndpoint = action === 'approve' ? 'approve' : 'decline';
    const payload = {
      group_id: group.group_id,
      comment: comment || undefined,
    };

    try {
      const response = await fetchData(
        `${envStore.apiUrl}/manager/${restaurantId.value}/team/${group.user.id}/days-off/${actionEndpoint}`,
        'POST',
        payload,
      );

      if (response.error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('dayOff.errorProcessingRequest'),
          life: 3000,
        });
        return { success: false, error: response.error };
      } else {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t(action === 'approve' ? 'dayOff.approvedSuccessfully' : 'dayOff.declinedSuccessfully'),
          life: 3000,
        });

        // Remove from pending approvals
        pendingApprovals.value = pendingApprovals.value.filter((approval) => approval.group_id !== group.group_id);

        // Reload approved days off if approved
        if (action === 'approve') {
          await fetchApprovedDaysOff();
        }

        return { success: true };
      }
    } catch (error) {
      console.error('Error processing day off request:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dayOff.errorProcessingRequest'),
        life: 3000,
      });
      return { success: false, error };
    }
  }

  // Confirmation helpers
  function confirmDeleteDaysOff(group: DayOffGroup, onConfirm?: () => void): void {
    const messageKey = group.isGroup ? 'dayOff.confirmDeleteGroup' : 'dayOff.confirmDelete';
    confirm.require({
      message: t(messageKey, { count: group.daysOff.length }),
      header: t('dayOff.deleteConfirmationHeader'),
      icon: 'pi pi-exclamation-triangle',
      rejectClass: 'p-button-secondary p-button-outlined',
      rejectLabel: t('common.cancel'),
      acceptLabel: t('common.delete'),
      accept: async () => {
        await deleteDaysOff(group.dates);
        onConfirm?.();
      },
    });
  }

  function confirmApprovalAction(
    group: DayOffApproval,
    action: 'approve' | 'decline',
    onConfirm?: (group: DayOffApproval, action: 'approve' | 'decline') => void,
  ): void {
    const actionText = action === 'approve' ? t('dayOff.approve') : t('dayOff.decline');
    const messageKey = action === 'approve' ? 'dayOff.confirmApprove' : 'dayOff.confirmDecline';

    confirm.require({
      message: t(messageKey, { userName: `${group.user.first_name} ${group.user.last_name}` }),
      header: actionText,
      icon: 'pi pi-exclamation-triangle',
      rejectClass: 'p-button-secondary p-button-outlined',
      rejectLabel: t('common.cancel'),
      acceptLabel: actionText,
      accept: () => {
        onConfirm?.(group, action);
      },
    });
  }

  function confirmDeleteApproved(userGroup: ApprovedDaysOffByUser, approvedGroup: ApprovedDayOffGroup, onConfirm?: () => void): void {
    confirm.require({
      message: t('dayOff.confirmDeleteApproved', {
        userName: `${userGroup.user.first_name} ${userGroup.user.last_name}`,
        count: approvedGroup.dates.length,
      }),
      header: t('dayOff.deleteApprovedDaysOff'),
      icon: 'pi pi-exclamation-triangle',
      rejectClass: 'p-button-secondary p-button-outlined',
      rejectLabel: t('common.cancel'),
      acceptLabel: t('common.delete'),
      accept: async () => {
        const dates = approvedGroup.dates.map((d) => new Date(d));
        await deleteDaysOff(dates, userGroup.user.id);

        // Remove from local state
        const userIndex = approvedDaysOff.value.findIndex((u) => u.user.id === userGroup.user.id);
        if (userIndex !== -1) {
          const groupIndex = approvedDaysOff.value[userIndex].approved_days_off_groups.findIndex((g) => g.group_id === approvedGroup.group_id);
          if (groupIndex !== -1) {
            approvedDaysOff.value[userIndex].approved_days_off_groups.splice(groupIndex, 1);
            // If no more groups for this user, remove the user from the list
            if (approvedDaysOff.value[userIndex].approved_days_off_groups.length === 0) {
              approvedDaysOff.value.splice(userIndex, 1);
            }
          }
        }

        onConfirm?.();
      },
    });
  }

  return {
    // State
    userDaysOff,
    daysOffSummary,
    loadingDaysOff,
    pendingApprovals,
    approvedDaysOff,
    loadingPending,
    loadingApproved,

    // Computed
    user,
    restaurantId,
    visibleDaysOff,
    userDaysOffDates,
    groupedDaysOff,
    remainingDaysOff,
    shouldShowLimitInfo,

    // Utility functions
    calculateDaysToTake,
    isValidDaysOffRequest,
    generateDisabledDatesForCalendar,
    generateDisabledDaysOfWeek,

    // API functions
    fetchUserDaysOff,
    addDaysOff,
    deleteDaysOff,
    fetchPendingApprovals,
    fetchApprovedDaysOff,
    approveOrDeclineDaysOff,

    // Confirmation helpers
    confirmDeleteDaysOff,
    confirmApprovalAction,
    confirmDeleteApproved,
  };
}
